import React from 'react';

import InputTextField from 'components/inputfields/InputTextField';
import { FORBIDDEN_INGREDIENTS } from 'components/constants';
import IProduct from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import { useDebouncedStateString } from 'hooks/useDebouncedState';

import { isCosmetic, isOTC, isSunscreen, isWhiteSnus } from './category';
import { isBlank } from 'utils/helpers-ts';

interface INonFoodIngredientsProps {
  readOnly?:boolean;
  approve: boolean;
  state: IProduct;
  setState: (state: IProduct | ((prevState: IProduct) => IProduct)) => void;
  rootCategory?: IFoodlaCategoryOption;
  isCreatingNew?: boolean;
}

// Check for forbidden ingredients
export const checkForbiddenIngredients = (ingredientStatement: string | undefined): string[] => {
  if (!ingredientStatement) return [];

  // Convert to lowercase for case-insensitive comparison
  const lowerCaseIngredients = ingredientStatement.toLowerCase();
  // Also create a version with normalized spacing for matching phrases with variable spacing
  const normalizedIngredients = lowerCaseIngredients
    .replace(/[\s()]+/g, ' ') // Normalize spaces and remove parentheses
    .replace(/\(\s*/g, '(') // Remove space after opening parenthesis
    .replace(/\s*\)/g, ')'); // Remove space before closing parenthesis

  // Split the ingredients into words and clean them for word-by-word matching
  const words = lowerCaseIngredients
    .replace(/[.,;()]/g, ' ') // Replace punctuation with spaces
    .split(/\s+/); // Split by whitespace

  // Check for each forbidden ingredient and collect all that are found
  const foundIngredients: string[] = [];

  for (const ingredient of FORBIDDEN_INGREDIENTS) {
    const lowerIngredient = ingredient.toLowerCase();
    // Create normalized versions of the ingredient for flexible matching
    const normalizedIngredient = lowerIngredient
      .replace(/[\s()]+/g, ' ')
      .replace(/\(\s*/g, '(')
      .replace(/\s*\)/g, ')');
    // Also create version with spaces removed completely for matching variants like "gold(nano)"
    const noSpaceIngredient = lowerIngredient.replace(/\s+/g, '');

    // For multi-word ingredients, check all variations
    if (lowerIngredient.includes(' ')) {
      if (
        lowerCaseIngredients.includes(lowerIngredient) || // Exact match
        normalizedIngredients.includes(normalizedIngredient) || // Match with normalized spacing
        lowerCaseIngredients.includes(noSpaceIngredient)
      ) {
        // Match with no spaces
        foundIngredients.push(ingredient);
      }
    } else {
      // For single-word ingredients, check for exact word matches
      if (
        words.includes(lowerIngredient) ||
        // Also check for word boundaries in the original text
        new RegExp(`\\b${lowerIngredient}\\b`, 'i').test(lowerCaseIngredients)
      ) {
        foundIngredients.push(ingredient);
      }
    }
  }

  return foundIngredients;
};

export const NonFoodIngredients = ({
  readOnly,
  approve,
  state,
  setState,
  rootCategory,
  isCreatingNew,
}: INonFoodIngredientsProps) => {
  // State to track forbidden ingredients
  const [forbiddenIngredients, setForbiddenIngredients] = React.useState<string[]>([]);
  const [focused, setFocused] = React.useState(false);

  // Use debounced state hook for immediate UI feedback with debounced parent updates
  const [localValue, handleDebouncedChange] = useDebouncedStateString(
    state.non_food_ingredients || '',
    setState,
    'non_food_ingredients',
    300
  );

  const requireError = React.useMemo(() => {
    if (isCreatingNew && !focused && !state.isDraft) {
      return '';
    }

    if (isWhiteSnus(state?.foodlaCategory, rootCategory) && isBlank(localValue)) {
      return 'Vänligen fyll i detta fält eller spara som utkast';
    }
    return '';
  }, [isCreatingNew, localValue, focused, state, rootCategory]);

  // Check for forbidden ingredients when ingredients change
  React.useEffect(() => {
    const foundForbidden = checkForbiddenIngredients(state.non_food_ingredients);
    setForbiddenIngredients(foundForbidden);
  }, [state.non_food_ingredients]);

  // TODO any
  const handleChange = ({ target }: any) => {
    handleDebouncedChange(target.value);
  };

  return (
    <>
      <InputTextField<IProduct>
        readOnly={readOnly}
        multiline
        spellCheck
        approve={approve}
        state={state}
        setState={setState}
        label="Innehållsförteckning"
        dense={false}
        hideComponentLabel={readOnly}
        inputName={'non_food_ingredients'}
        value={localValue}
        multilineRows={2}
        multilineMax={20}
        onChange={handleChange}
        onBlur={() => setFocused(true)}
        withLabel
        forceRed={forbiddenIngredients.length > 0 || !!requireError}
        labelOptions={{
          heading: 'Innehållsförteckning',
          required:
            isCosmetic(state?.foodlaCategory, rootCategory) ||
            isSunscreen(state?.foodlaCategory, rootCategory) ||
            isOTC(state?.foodlaCategory, rootCategory),
        }}
        helpText={
          requireError || 'Obligatoriskt för att aktivera produkten, men kan sparas som utkast utan att fyllas i.'
        }
      />
      {forbiddenIngredients.length > 0 && (
        <span style={{ fontWeight: 'bold', color: 'orangered' }}>
          {forbiddenIngredients.length === 1
            ? `"${forbiddenIngredients[0]}" är förbjuden eller omfattas av strikta begränsningar inom EU.`
            : `Följande ämnen är förbjudna eller omfattas av strikta begränsningar inom EU: ${forbiddenIngredients.join(
                ', '
              )}.`}{' '}
          Kontrollera den aktuella lagstiftningen innan fortsatt användning.
        </span>
      )}
    </>
  );
};

export default NonFoodIngredients;
